/**
 * MainAppNav — the cross-origin twin-navigation icons (Patients, Knowledge Base,
 * Admin/Analytics) shown in every satellite app header (care-planner / compatibility /
 * alg), to the RIGHT of the title.
 *
 * Mirrors the same three glyphs the main digital-twin Home header renders, but
 * the satellite apps live on sibling subdomains (`care-planner.<env>.tricorder.icu`,
 * etc.), so these targets are cross-origin: react-router cannot route across
 * origins. Each icon is therefore a hard `window.location.assign` to the derived
 * twin route — the same grammar as the always-on `ReturnToMainAgent` button it
 * sits beside, sharing the `mainAppUrl(path)` source of truth.
 *
 * Lives in `frontend/src/components/` alongside `ReturnToMainAgent` so the
 * satellite bundles consume it via `@frontend/...` (npm + `@frontend` + type
 * resolution all work from here; a shared dir outside `frontend/` has no
 * node_modules in its module-resolution walk).
 *
 * Design (DESIGN.md): icon-only utility glyphs in the back-chevron grammar (NOT
 * the +New pill) — 44px touch targets for the bedside + iOS Safari, token
 * surfaces, single Action-Blue accent via the focus ring, `active:scale-95`
 * system press. Labels are i18n aria-labels only (no visible copy;
 * non-paternalistic).
 */

import { BarChart3, FileText, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { mainAppUrl } from '@/api/mainAppUrl';
import { MAIN_APP_ROUTES } from '@/lib/routes';
import { cn } from '@/lib/utils';

export interface MainAppNavProps {
  /** Extra classes for per-header alignment (e.g. negative margins). */
  className?: string;
}

interface NavTarget {
  path: string;
  Icon: typeof Users;
  /** i18n key + inline English default for the icon's accessible name. */
  labelKey: string;
  labelDefault: string;
}

// Same three twin routes the main-app Home header links to. Cross-origin from a
// satellite, so navigation is a hard location.assign via mainAppUrl(path).
const TARGETS: readonly NavTarget[] = [
  {
    path: MAIN_APP_ROUTES.documents,
    Icon: FileText,
    labelKey: 'documents.list.title',
    labelDefault: 'Knowledge Base',
  },
  {
    path: MAIN_APP_ROUTES.adminAnalytics,
    Icon: BarChart3,
    labelKey: 'home.admin',
    labelDefault: 'Admin',
  },
];

export function MainAppNav({ className }: MainAppNavProps) {
  const { t } = useTranslation();

  return (
    <nav className={cn('flex items-center gap-0.5', className)} data-testid="main-app-nav">
      {TARGETS.map(({ path, Icon, labelKey, labelDefault }) => {
        const label = t(labelKey, labelDefault);
        return (
          <button
            key={path}
            type="button"
            onClick={() => window.location.assign(mainAppUrl(path))}
            aria-label={label}
            title={label}
            // 36px (w-9) matches the main-app Home header's identical glyphs —
            // visually faithful to that nav and leaves room for the title + CTA
            // beside the back-to-twin button on a 375px bedside iPhone.
            className={cn(
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[var(--color-surface-mute,#6B7280)] transition',
              'active:scale-95 hover:bg-[var(--color-surface-canvas,#F3F4F6)] hover:text-[var(--color-surface-ink,#1F2937)]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)]',
            )}
          >
            <Icon className="h-5 w-5" aria-hidden="true" />
          </button>
        );
      })}
    </nav>
  );
}
